// One-click starting points (UI-4). A preset is only a definition — loading
// one fills the rows, and from there it is the marketer's to change.
//
// Four kinds, said apart on the screen because they answer different
// questions: the campaign audience during the Klaviyo handover (the wave
// stamp), the rules `waves assign` stamps by (who the next wave takes over),
// the segments every Klaviyo account has, and (5.5) the per-message figures
// of our own sends.

import { useState } from "react";
import { ArrowRightLeftIcon, FlameIcon, MailIcon, SparklesIcon } from "lucide-react";
import type { Definition } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { Callout } from "@/components/emc/states";
import { PRESETS } from "@/pages/segments/model";
import type { Preset } from "@/pages/segments/model";

interface Props {
  /** The definition to load, and the name to offer for a new segment. */
  onPick: (definition: Definition, name: string) => void;
  disabled?: boolean;
}

function PresetCard({ preset, onPick, disabled }: { preset: Preset } & Props) {
  const parameter = preset.parameter;
  const [value, setValue] = useState(parameter?.initial ?? 0);
  const options = parameter
    ? Array.from({ length: parameter.max - parameter.min + 1 }, (_, index) => parameter.min + index)
    : [];
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-card border border-line bg-surface p-3">
      <div className="flex flex-col gap-1">
        <span className="font-medium text-ink">{parameter ? parameter.nameFor(value) : preset.name}</span>
        <span className="text-label text-muted-foreground">{preset.description}</span>
      </div>
      <div className="mt-auto flex flex-wrap items-center gap-2">
        {parameter && (
          <NativeSelect
            aria-label={parameter.label}
            size="sm"
            value={value}
            disabled={disabled}
            onChange={(event) => setValue(Number(event.target.value))}
          >
            {options.map((option) => (
              <option key={option} value={option}>
                {`N = ${option}`}
              </option>
            ))}
          </NativeSelect>
        )}
        <Button
          variant="outline"
          size="sm"
          type="button"
          disabled={disabled}
          onClick={() =>
            parameter
              ? onPick(parameter.definitionFor(value), parameter.nameFor(value))
              : onPick(preset.definition, preset.name)
          }
        >
          Betöltés
        </Button>
      </div>
    </div>
  );
}

function Grid({ presets, ...props }: { presets: Preset[] } & Props) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(var(--emc-tile-min),1fr))] gap-3">
      {presets.map((preset) => (
        <PresetCard key={preset.key} preset={preset} {...props} />
      ))}
    </div>
  );
}

export function Presets({ onPick, disabled }: Props) {
  const of = (group: Preset["group"]) => PRESETS.filter((preset) => preset.group === group);
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <ArrowRightLeftIcon className="size-4 text-primary" />
          <h3>Kampány-közönség a migráció alatt</h3>
        </div>
        <p className="text-muted-foreground">
          Ezt válaszd egy rendes kampány közönségének, amíg a Klaviyótól hullámonként vesszük át a listát. A hullám-bélyeg
          szerint válogat: akit az 1–N. hullámban átvettünk, az nálunk kap levelet, a Klaviyo pedig pontosan őket zárja ki
          (emc_wave ≤ N). Aki azóta inaktív lett, az is benne marad; akinek nincs hulláma, az még a Klaviyóé.
        </p>
        <Grid presets={of("migration")} onPick={onPick} disabled={disabled} />
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <FlameIcon className="size-4 text-warn" />
          <h3>Kit vesz át a következő hullám</h3>
        </div>
        <p className="text-muted-foreground">
          A waves assign szabályai, közönséges feltételekként: megmutatják, kit bélyegez a következő hullám, és hány
          kontaktot. Kampány-közönségnek a migráció alatt ne ezeket használd — az aktivitás naponta változik, a bélyeg nem,
          és a Klaviyo a bélyeg szerint zár ki. A szabályok egymásba ágyazódnak (aki 30 napon belül kattintott, 90 napon
          belül is aktív). A hullám kampányában válaszd az engaged-first sorrendet.
        </p>
        <Grid presets={of("warmup")} onPick={onPick} disabled={disabled} />
        <Callout>
          Az új bélyeget a waves sync adja át a Klaviyónak, és a kontakt csak ezután kerül az „Átvett hullámok”
          közönségébe — addig a Klaviyo küld neki. Ezért a sync a bélyegzés után, a küldés előtt fusson.
        </Callout>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <SparklesIcon className="size-4 text-muted-foreground" />
          <h3>Gyakori Klaviyo-szegmensek</h3>
        </div>
        <Grid presets={of("klaviyo")} onPick={onPick} disabled={disabled} />
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <MailIcon className="size-4 text-muted-foreground" />
          <h3>A saját leveleink alapján</h3>
        </div>
        <p className="text-muted-foreground">
          Levelenként, csak a saját küldéseinkből, éjszakánként frissítve: kapott-e tőlünk levelet, és a leveleink
          közül hányat nyitott meg ember.
        </p>
        <Grid presets={of("letters")} onPick={onPick} disabled={disabled} />
      </section>
    </div>
  );
}
